<template>
    <article class="apartment">
        <ContentHero
            :background-color="entry?.contentHero?.[0]?.backgroundColor"
            :image-position="entry?.contentHero?.[0]?.imagePosition || 'wide'"
            :image="entry?.contentHero?.[0]?.image?.[0]"
            :pending="pending"
        >
            <template #title>{{ entry?.title }}</template>
            <template #description>{{ entry?.contentHero?.[0]?.description }}</template>
            <template #caption>{{ entry?.contentHero?.[0]?.caption }}</template>
        </ContentHero>

        <BaseSection
            v-if="pending || entry?.amenitiesBlock?.[0]?.title"
            :pending="pending"
        >
            <template #title>
                {{ entry?.amenitiesBlock?.[0]?.title }}
            </template>

            <template
                v-if="entry?.amenitiesBlock?.[0]?.description"
                #intro
            >
                {{ entry?.amenitiesBlock?.[0]?.description }}
            </template>

            <template
                v-if="entry?.amenitiesBlock?.[0]?.amenities?.length"
                #default
            >
                <BaseExpandableList
                    :items="entry?.amenitiesBlock?.[0]?.amenities"
                >
                    <template #default="slotProps">
                        <BaseIcon
                            v-if="slotProps?.item?.amenityIcons"
                            :icon="kebabCase(slotProps?.item?.amenityIcons?.toLowerCase())"
                        />
                        <span class="apartment__amenity-label">{{ slotProps?.item?.title }}</span>
                    </template>

                    <template #expand-button>
                        <span>{{ $t('see-all-amenities') }}</span>

                        <BaseIcon icon="plus" />
                    </template>
                </BaseExpandableList>
            </template>
        </BaseSection>

        <CarousselBlock
            v-if="entry?.carousselBlock?.[0]?.title || pending"
            variant="apartment"
            :caroussel-items="entry?.carousselBlock?.[0]?.carousselItems"
            :background-color="entry?.carousselBlock?.[0]?.backgroundColor"
            :pending="pending"
        >
            <template
                v-if="entry?.carousselBlock?.[0]?.title"
                #title
            >
                {{ entry?.carousselBlock?.[0]?.title }}
            </template>

            <template
                v-if="entry?.carousselBlock?.[0]?.description"
                #description
            >
                {{ entry?.carousselBlock?.[0]?.description }}
            </template>
        </CarousselBlock>

        <IllustrationGrid
            v-if="entry?.illustrationGrid?.[0]?.title"
            :items="entry?.illustrationGrid?.[0]?.illustrationItems"
        >
            <template #title>{{ entry?.illustrationGrid?.[0]?.title }}</template>
            <template #description>{{ entry?.illustrationGrid?.[0]?.description }}</template>

            <IllustrationItem
                v-for="illustration in entry?.illustrationGrid?.[0]?.illustrationItems"
                :key="illustration?.title"
                :illustration="illustration"
                :class="illustration?.sticker?.length > 12 ? 'sticker__title--small' : ''"
            >
                <template
                    v-if="illustration?.sticker"
                    #offer
                >
                    {{ illustration?.sticker }}
                </template>
                <template #title>
                    {{ illustration?.title }}
                </template>
                <template #description>
                    {{ illustration?.description }}
                </template>
                <template #cta>
                    {{ illustration?.linkField?.text }}
                </template>
            </IllustrationItem>
        </IllustrationGrid>

        <BaseSection
            v-if="entry?.faq?.[0]?.items?.length || pending"
            :pending="pending"
        >
            <template
                v-if="entry?.faq?.[0]?.title"
                #title
            >
                {{ entry?.faq?.[0]?.title }}
            </template>
            <BaseAccordion :items="entry?.faq?.[0]?.items" />
        </BaseSection>

        <RelatedItems
            :background-color="entry?.relatedItems?.[0]?.backgroundColor"
            :items="entry?.relatedItems?.[0]?.entries"
            :pending="pending"
        >
            <template
                v-if="entry?.relatedItems?.[0]?.title"
                #title
            >
                {{ entry.relatedItems[0].title }}
            </template>

            <template
                v-if="entry?.relatedItems?.[0]?.caption"
                #caption
            >
                {{ entry.relatedItems[0].caption }}
            </template>
        </RelatedItems>

        <ApartmentListItems
            v-if="pending || entry?.crossSell?.[0]?.title"
            background-color="creme"
            class="apartment-list-items--spacious"
            :apartments="entry?.crossSell?.[0]?.apartments"
            :pending="pending"
        >
            <template
                v-if="entry?.crossSell?.[0]?.title"
                #header
            >
                <h2>{{ entry?.crossSell?.[0]?.title }}</h2>
            </template>
        </ApartmentListItems>

        <LazyBaseTextImage
            v-if="entry?.textImage?.[0]?.title || pending"
            class="base-text-image--plain-link"
            :background-color="entry?.textImage?.[0]?.backgroundColor"
            :image="entry?.textImage?.[0]?.image?.[0]"
            :pending="pending"
            :landscape="true"
            :use-parallax="true"
            hydrate-on-visible
        >
            <template #title>
                {{ entry?.textImage?.[0]?.title }}
            </template>

            <template
                #text
            >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="entry?.textImage?.[0]?.text?.html" />
            </template>
        </LazyBaseTextImage>
    </article>
</template>

<script setup>
import apartmentQuery from '~/graphql/queries/apartment.graphql';

import { kebabCase } from 'lodash-es';

const { variables } = useCraftGraphql();
const response = await useLazyAsyncQuery({
    query: apartmentQuery,
    variables
});
const pending = response.pending;

const { craftEntry: entry } = useCraftPage(response);
</script>

<style lang="less">
.apartment__amenity-label {
    transform: translateY(1px);

    @media @q-md-min {
        /* Offset for baseline alignment */
        transform: translateY(2px);
    }
}
</style>
